import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

/* ═══════════════════════════════════════════════════════════════════════
   LES GRAPHIQUES DU MODÈLE « ADMIN ASSO-PRO v3 »

   Portage direct, en SVG, des quatre tracés du modèle : barres, jauge,
   tendance et anneau. Rien n'est réinventé — les marges, les rayons, les
   trames, les durées d'animation sont celles relevées dans le modèle.

   Pourquoi pas la bibliothèque de graphiques déjà présente : elle impose
   ses propres marges, ses propres étiquettes et ses propres infobulles.
   On y arrivait « à peu près », et « à peu près » est précisément ce
   qu'on cherche à éviter ici.

   Trois règles de lecture, communes aux quatre :
     — la moyenne est tracée, sinon une hauteur ne se compare à rien ;
     — ce qui est sous la moyenne est HACHURÉ, pas seulement d'une autre
       couleur : la teinte seule exclut qui distingue mal le vert ;
     — le survol met en avant plutôt qu'il ne colore : le reste s'estompe.
   ═══════════════════════════════════════════════════════════════════════ */

export const P = {
  vert: '#5EBD4F', vertF: '#2F7A2A', vertC: '#A7DA9C', vertP: '#EAF6E7',
  nuit: '#102538', nuit2: '#22405C', noir: '#0B1620',
  texte: '#2E465D', doux: '#5A7288', grille: '#E9EFEB', fond: '#F3F6F4',
  alerte: '#B3372A', attention: '#8A5300',
};


/* Une graduation « ronde » : on cherche le pas le plus proche parmi des
   valeurs que l'œil lit sans effort (1, 2, 2,5, 3, 4, 5, 10 fois une
   puissance de dix). Sans cela l'axe afficherait 0 / 3,7 / 7,4… */
export function niceMax(max) {
  if (!max || max <= 0) return { pas: 1, top: 4 };
  const brut = max / 4;
  const k = Math.pow(10, Math.floor(Math.log10(brut)));
  const m = brut / k;
  const pas = k * [1, 2, 2.5, 3, 4, 5, 10].find((c) => c >= m);
  return { pas, top: pas * 4 };
}

/* Catmull-Rom converti en Bézier : chaque point tire ses poignées du
   milieu de ses deux voisins. */
export function lisser(p) {
  if (p.length < 2) return p.length ? `M${p[0][0]},${p[0][1]}` : '';
  let d = `M${p[0][0]},${p[0][1]}`;
  for (let i = 0; i < p.length - 1; i += 1) {
    const a = p[i - 1] || p[i];
    const b = p[i];
    const c = p[i + 1];
    const e = p[i + 2] || c;
    d += ` C${b[0] + (c[0] - a[0]) / 6},${b[1] + (c[1] - a[1]) / 6}`
       + ` ${c[0] - (e[0] - b[0]) / 6},${c[1] - (e[1] - b[1]) / 6}`
       + ` ${c[0]},${c[1]}`;
  }
  return d;
}

/* ── Abréger sans rendre indiscernable ───────────────────────────────
   « Gestion administrative, financière et des ressources matérielles »
   ne tient pas au bout d'un axe de radar. On raccourcit donc, mais deux
   pièges guettent :

     — couper à la première virgule ou au premier « et » fabrique des
       doublons. « Services aux membres et Activités Commerciales » et
       « … Commerciales sociales » se réduisent tous deux à « Services
       aux membres » : deux axes deviennent indiscernables, ce qui est
       pire qu'un débordement ;
     — rallonger ne sert à rien si la coupe a lieu AVANT la comparaison,
       puisque la différence est justement dans la partie jetée.

   D'où l'ordre retenu : on ne raccourcit à la tête que si les têtes
   sont déjà toutes distinctes ; sinon on part du nom entier, et c'est
   la troncature qui s'allonge jusqu'à ce que les libellés diffèrent.

   Le nom complet reste dans l'infobulle : rien n'est perdu.
*/
const _tete = (nom) => (nom.split(/,| et | & /i)[0].trim() || nom.trim());

const _tronquer = (nom, limite) => {
  if (nom.length <= limite) return nom;
  const mots = nom.split(/\s+/);
  let court = mots[0].slice(0, limite);
  for (let i = 1; i < mots.length && `${court} ${mots[i]}`.length <= limite; i += 1) {
    court += ` ${mots[i]}`;
  }
  return `${court}…`;
};

/* Rang du premier mot qui distingue deux libelles voisins. Hors de la boucle
   a dessein : une fermeture qui capture une variable de boucle en lit la
   valeur finale, et le rang doit etre fige quand on s'en sert. */
function _premierEcart(mots, plusLong) {
  for (let rang = 0; rang < plusLong; rang += 1) {
    const premier = mots[0][rang] || '';
    if (mots.some((m) => (m[rang] || '') !== premier)) return rang;
  }
  return plusLong;
}

export function abregerListe(noms, max = 20) {
  const tetes = noms.map(_tete);
  const combien = new Map();
  tetes.forEach((x) => combien.set(x, (combien.get(x) || 0) + 1));

  // La tête suffit quand elle est unique ; sinon on repart du nom entier.
  // Faire basculer TOUTE la liste au nom entier parce qu'une seule paire
  // se ressemble rallongerait les libellés qui allaient très bien.
  const bases = noms.map((n, i) => (combien.get(tetes[i]) === 1 ? tetes[i] : n));
  const courts = bases.map((b) => _tronquer(b, max));

  // Reste les libellés que la troncature a rendus identiques. Leur
  // différence est plus loin dans le nom -- au-delà de ce qui tient à
  // l'écran -- donc l'allonger ne sert à rien : on ramène le mot qui
  // distingue EN TÊTE du libellé, là où il se verra.
  const paquets = new Map();
  courts.forEach((c, i) => paquets.set(c, (paquets.get(c) || []).concat(i)));
  paquets.forEach((groupe) => {
    if (groupe.length < 2) return;
    const mots = groupe.map((i) => bases[i].split(/\s+/));
    const plusLong = Math.max(...mots.map((m) => m.length));
    const rang = _premierEcart(mots, plusLong);
    groupe.forEach((i, k) => {
      const marque = mots[k][rang];
      if (!marque) return;                    // ce membre garde le libellé nu
      courts[i] = `${_tronquer(bases[i], Math.max(6, max - marque.length - 2))} ${marque}`;
    });
  });
  return courts;
}

export function replier(texte, parLigne = 14, lignes = 2) {
  const mots = texte.split(/\s+/);
  const out = [];
  let courante = '';
  for (const mot of mots) {
    if (!courante) { courante = mot; continue; }
    if (`${courante} ${mot}`.length <= parLigne) courante += ` ${mot}`;
    else { out.push(courante); courante = mot; }
    if (out.length === lignes) break;
  }
  if (courante && out.length < lignes) out.push(courante);
  else if (courante) out[out.length - 1] += '…';   // il restait du texte
  return out.length ? out : [texte];
}

let compteurId = 0;
const nouvelId = (prefixe) => { compteurId += 1; return `${prefixe}-${compteurId}`; };

/* La trame à 45° qui marque « sous la moyenne ». */
function Hachure({ id, fond = '#FFFFFF' }) {
  return (
    <pattern id={id} patternUnits="userSpaceOnUse" width="7" height="7"
             patternTransform="rotate(45)">
      <rect width="7" height="7" fill={fond} />
      <rect width="2.4" height="7" fill="#B9C7BF" />
    </pattern>
  );
}

/* Mesure la largeur réelle du conteneur : un graphique en SVG a besoin
   d'un nombre, pas d'un pourcentage. `ResizeObserver` plutôt qu'un
   écouteur de redimensionnement, qui manquerait l'ouverture de la barre
   latérale — la fenêtre ne change pas de taille, la colonne si. */
export function useLargeur() {
  const ref = useRef(null);
  const [largeur, setLargeur] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([e]) => setLargeur(Math.round(e.contentRect.width)));
    ro.observe(el);
    setLargeur(Math.round(el.getBoundingClientRect().width));
    return () => ro.disconnect();
  }, []);
  return [ref, largeur];
}

/* Le compteur qui monte. Il s'arrête net si la personne a demandé moins
   d'animations : `prefers-reduced-motion` n'est pas un détail de confort
   pour qui souffre de troubles vestibulaires. */
export function useCompteur(cible, duree = 900) {
  const [valeur, setValeur] = useState(0);
  useEffect(() => {
    const sobre = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (sobre) { setValeur(cible); return undefined; }
    let raf;
    const depart = performance.now();
    const pas = (t) => {
      const p = Math.min(1, (t - depart) / duree);
      setValeur(cible * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(pas);
    };
    raf = requestAnimationFrame(pas);
    return () => cancelAnimationFrame(raf);
  }, [cible, duree]);
  return valeur;
}

function Infobulle({ x, y, children }) {
  return (
    <div style={{ position: 'absolute', left: x, top: y, transform: 'translate(-50%,-100%)',
      padding: '7px 10px', borderRadius: 10, background: P.noir, color: '#FFFFFF',
      fontSize: 12, whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 5,
      boxShadow: '0 10px 24px -12px rgba(11,22,32,.6)' }}>
      {children}
    </div>
  );
}

/* ── Barres ───────────────────────────────────────────────────────────
   La plus haute passe en encre pleine (le record), celles sous la
   moyenne sont hachurées, et un repère chiffré porte la moyenne. */
export function Barres({ donnees, hauteur = 236, suffixe = '', axes = true }) {
  const [ref, w] = useLargeur();
  const [survol, setSurvol] = useState(null);
  const id = useRef(nouvelId('hb')).current;

  const valeurs = donnees.map((d) => Number(d.valeur) || 0);
  if (!valeurs.length) valeurs.push(0);

  const mL = axes ? 42 : 2, mR = 4, mT = axes ? 30 : 6, mB = axes ? 26 : 4;
  const iw = Math.max(0, w - mL - mR), ih = hauteur - mT - mB;
  const { pas, top } = niceMax(Math.max(...valeurs));
  const moyenne = valeurs.reduce((a, b) => a + b, 0) / valeurs.length;
  const maxi = Math.max(...valeurs);
  const ecart = iw / valeurs.length;
  const bw = Math.min(40, ecart * 0.66);
  const y = (v) => mT + ih - (v / top) * ih;

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      {w > 0 && (
        <svg width={w} height={hauteur} viewBox={`0 0 ${w} ${hauteur}`}
             style={{ display: 'block', overflow: 'visible' }} role="img"
             aria-label="Histogramme">
          <defs><Hachure id={id} /></defs>

          {axes && [0, 1, 2, 3, 4].map((g) => {
            const vy = mT + (ih * g) / 4;
            return (
              <g key={g}>
                <line x1={mL} x2={w - mR} y1={vy} y2={vy} stroke={P.grille}
                      strokeDasharray={g === 4 ? '' : '2 5'} />
                <text x={mL - 10} y={vy + 4} textAnchor="end" fontSize="11" fill={P.doux}>
                  {Math.round(top - pas * g)}
                </text>
              </g>
            );
          })}

          {valeurs.map((v, i) => {
            const cx = mL + ecart * i + ecart / 2;
            const bh = Math.max(bw, (v / top) * ih);
            const actif = survol === i;
            const bas = v < moyenne;
            return (
              <rect key={`b${i}`} x={cx - bw / 2} y={mT + ih - bh}
                    width={bw} height={bh} rx={bw / 2}
                    fill={v === maxi && maxi > 0 ? P.nuit : bas ? `url(#${id})` : P.vert}
                    stroke={bas ? '#D5DFD9' : 'none'}
                    style={{ transformBox: 'fill-box', transformOrigin: '50% 100%',
                      opacity: survol == null || actif ? 1 : 0.5,
                      transition: 'opacity .2s' }} />
            );
          })}

          {axes && donnees.map((d, i) => (
            <text key={`x${i}`} x={mL + ecart * i + ecart / 2} y={hauteur - 7}
                  textAnchor="middle" fontSize="11"
                  fill={survol === i ? P.nuit : P.doux}
                  fontWeight={survol === i ? 600 : 400}>
              {d.label}
            </text>
          ))}

          <line x1={mL} x2={w - mR} y1={y(moyenne)} y2={y(moyenne)}
                stroke={P.nuit} strokeDasharray="5 4" opacity=".4" />
          {axes && (
            <>
              <rect x={w - mR - 66} y={y(moyenne) - 11} width="66" height="22" rx="11" fill={P.nuit} />
              <text x={w - mR - 33} y={y(moyenne) + 4} textAnchor="middle"
                    fontSize="11" fontWeight="600" fill="#FFFFFF">
                moy. {Math.round(moyenne)}
              </text>
            </>
          )}

          {valeurs.map((v, i) => (
            <rect key={`z${i}`} x={mL + ecart * i} y="0" width={ecart} height={hauteur}
                  fill="transparent"
                  onMouseEnter={() => setSurvol(i)} onMouseLeave={() => setSurvol(null)} />
          ))}
        </svg>
      )}

      {survol != null && axes && (
        <Infobulle x={mL + ecart * survol + ecart / 2} y={y(valeurs[survol]) - 2}>
          <b style={{ fontSize: 13 }}>
            {valeurs[survol].toLocaleString('fr-FR')} {suffixe}
          </b>
          <span style={{ color: 'rgba(255,255,255,.65)' }}> · {donnees[survol].label}</span>
        </Infobulle>
      )}
    </div>
  );
}

/* ── Jauge en demi-cercle ─────────────────────────────────────────────
   Elle répond à « que deviennent les demandes ? ». Les graduations
   autour donnent l'échelle sans chiffres supplémentaires. */
export function Jauge({ segments, petite = false }) {
  const [ref, w] = useLargeur();
  const [survol, setSurvol] = useState(null);
  const id = useRef(nouvelId('hg')).current;

  const total = segments.reduce((a, s) => a + s.valeur, 0) || 1;
  const L = Math.min(w || 1, petite ? 210 : 320);
  const sw = Math.round(L * 0.12);
  const r = L / 2 - sw / 2 - 8;
  const cx = (w || L) / 2;
  const cy = r + sw / 2 + 8;
  const H = cy + 4;
  const d = `M${cx - r},${cy} A${r},${r} 0 0 1 ${cx + r},${cy}`;

  const courant = segments[survol != null ? survol : 0] || { valeur: 0, nom: '' };
  const pourcent = Math.round((courant.valeur / total) * 100);
  const anime = useCompteur(pourcent);

  let decalage = 0;

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%', height: H }}>
      {w > 0 && r > 0 && (
        <svg width={w} height={H} style={{ display: 'block', overflow: 'visible' }}
             role="img" aria-label="Répartition">
          <defs><Hachure id={id} /></defs>
          <path d={d} fill="none" stroke={P.fond} strokeWidth={sw} />

          {Array.from({ length: 21 }, (_, t) => {
            const a = Math.PI * (1 - t / 20);
            const r1 = r + sw / 2 + 3;
            const r2 = r1 + (t % 5 === 0 ? 6 : 3);
            return (
              <line key={t} x1={cx + Math.cos(a) * r1} y1={cy - Math.sin(a) * r1}
                    x2={cx + Math.cos(a) * r2} y2={cy - Math.sin(a) * r2}
                    stroke="#C9D5CE" strokeWidth="1.2" />
            );
          })}

          {segments.map((sg, i) => {
            const part = (sg.valeur / total) * 100;
            const actif = survol === i;
            const el = (
              <path key={i} d={d} fill="none"
                    stroke={sg.hachure ? `url(#${id})` : sg.couleur}
                    strokeWidth={actif ? sw + 6 : sw}
                    pathLength="100"
                    strokeDasharray={`${Math.max(0.1, part - 0.9)} ${100 - part + 0.9 + 100}`}
                    strokeDashoffset={-decalage}
                    style={{ transition: 'stroke-width .2s', cursor: 'pointer',
                             opacity: survol == null || actif ? 1 : 0.55 }}
                    onMouseEnter={() => setSurvol(i)} onMouseLeave={() => setSurvol(null)} />
            );
            decalage += part;
            return el;
          })}
        </svg>
      )}

      <div style={{ position: 'absolute', left: '50%', bottom: 0,
                    transform: 'translateX(-50%)', textAlign: 'center',
                    pointerEvents: 'none' }}>
        <div style={{ fontSize: petite ? 28 : 44, fontWeight: 600,
                      letterSpacing: '-.04em', color: P.nuit, lineHeight: 1 }}>
          {survol != null ? pourcent : Math.round(anime)}
          <span style={{ fontSize: petite ? 16 : 22, fontWeight: 500 }}> %</span>
        </div>
        {!petite && (
          <div style={{ fontSize: 12, color: P.doux, marginTop: 4 }}>
            {(courant.nom || '').toLowerCase()} · {courant.valeur}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Tendance ─────────────────────────────────────────────────────────
   La courbe pleine est la période en cours, la pointillée la précédente.
   Sans ce repère, « 42 diagnostics » ne dit pas si c'est beaucoup. */
export function Tendance({ donnees, precedent, hauteur = 240, axes = true }) {
  const [ref, w] = useLargeur();
  const [survol, setSurvol] = useState(null);
  const id = useRef(nouvelId('gt')).current;

  const serie = donnees.map((d) => Number(d.valeur) || 0);
  const avant = (precedent && precedent.length === serie.length)
    ? precedent.map(Number) : null;
  const n = serie.length;
  if (n < 2) {
    return <div ref={ref} style={{ height: hauteur }} />;
  }

  const mL = axes ? 42 : 4, mR = axes ? 14 : 6, mT = axes ? 16 : 8, mB = axes ? 28 : 6;
  const iw = Math.max(0, w - mL - mR), ih = hauteur - mT - mB;
  const { pas, top } = niceMax(Math.max(...serie, ...(avant || [0])));
  const x = (i) => mL + (iw * i) / (n - 1);
  const y = (v) => mT + ih - (v / top) * ih;
  const pc = serie.map((v, i) => [x(i), y(v)]);
  const dc = lisser(pc);

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      {w > 0 && (
        <svg width={w} height={hauteur} viewBox={`0 0 ${w} ${hauteur}`}
             style={{ display: 'block', overflow: 'visible' }}
             role="img" aria-label="Évolution">
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={P.vert} stopOpacity=".3" />
              <stop offset="100%" stopColor={P.vert} stopOpacity="0" />
            </linearGradient>
          </defs>

          {axes && [0, 1, 2, 3, 4].map((g) => {
            const vy = mT + (ih * g) / 4;
            return (
              <g key={g}>
                <line x1={mL} x2={w - mR} y1={vy} y2={vy} stroke={P.grille}
                      strokeDasharray={g === 4 ? '' : '2 5'} />
                <text x={mL - 10} y={vy + 4} textAnchor="end" fontSize="11" fill={P.doux}>
                  {Math.round(top - pas * g)}
                </text>
              </g>
            );
          })}

          {axes && donnees.map((d, i) => (
            <text key={`x${i}`} x={x(i)} y={hauteur - 7} textAnchor="middle" fontSize="11"
                  fill={survol === i ? P.nuit : P.doux}
                  fontWeight={survol === i ? 600 : 400}>
              {d.label}
            </text>
          ))}

          <path d={`${dc} L${x(n - 1)},${mT + ih} L${x(0)},${mT + ih} Z`} fill={`url(#${id})`} />
          {avant && (
            <path d={lisser(avant.map((v, i) => [x(i), y(v)]))} fill="none"
                  stroke={P.doux} strokeWidth="1.6" strokeDasharray="4 5" opacity=".6" />
          )}
          <path d={dc} fill="none" stroke={P.vert} strokeWidth={axes ? 3 : 2.2}
                strokeLinecap="round" />

          <circle cx={x(n - 1)} cy={y(serie[n - 1])} r="5" fill="#FFFFFF"
                  stroke={P.vert} strokeWidth="2.5" />

          {survol != null && (
            <>
              <line x1={x(survol)} x2={x(survol)} y1={mT} y2={mT + ih}
                    stroke={P.nuit} strokeDasharray="3 3" opacity=".35" />
              <circle cx={x(survol)} cy={y(serie[survol])} r="6" fill={P.vert}
                      stroke="#FFFFFF" strokeWidth="3" />
            </>
          )}

          {serie.map((v, i) => (
            <rect key={`z${i}`} x={x(i) - iw / (n - 1) / 2} y="0"
                  width={iw / (n - 1)} height={hauteur} fill="transparent"
                  onMouseEnter={() => setSurvol(i)} onMouseLeave={() => setSurvol(null)} />
          ))}
        </svg>
      )}

      {survol != null && (
        <Infobulle x={x(survol)} y={y(serie[survol]) - 2}>
          <b style={{ fontSize: 13 }}>{serie[survol]}</b>
          <span style={{ color: 'rgba(255,255,255,.65)' }}> · {donnees[survol].label}</span>
        </Infobulle>
      )}
    </div>
  );
}

/* ── Anneau ───────────────────────────────────────────────────────────
   Au plus cinq parts : au-delà, les angles deviennent indiscernables et
   l'anneau ment sur les proportions. Le reste part dans « Autres ». */
export function Anneau({ segments, taille = 176, legende = '' }) {
  const [survol, setSurvol] = useState(null);
  const id = useRef(nouvelId('hd')).current;

  const total = segments.reduce((a, s) => a + s.valeur, 0) || 1;
  const sw = taille * 0.14;
  const r = taille / 2 - sw / 2 - 5;
  const c = taille / 2;
  const anime = useCompteur(total);
  const courant = survol != null ? segments[survol] : null;
  let decalage = 0;

  return (
    <div style={{ position: 'relative', width: taille, height: taille }}>
      <svg width={taille} height={taille} style={{ display: 'block', overflow: 'visible' }}
           role="img" aria-label="Répartition">
        <defs><Hachure id={id} /></defs>
        <circle cx={c} cy={c} r={r} fill="none" stroke={P.fond} strokeWidth={sw} />
        {segments.map((sg, i) => {
          const part = (sg.valeur / total) * 100;
          const actif = survol === i;
          const el = (
            <circle key={i} cx={c} cy={c} r={r} fill="none"
                    stroke={sg.hachure ? `url(#${id})` : sg.couleur}
                    strokeWidth={actif ? sw + 7 : sw}
                    pathLength="100"
                    strokeDasharray={`${Math.max(0.1, part - 1.4)} ${100 - part + 1.4}`}
                    strokeDashoffset={-decalage}
                    transform={`rotate(-90 ${c} ${c})`}
                    style={{ transition: 'stroke-width .2s', cursor: 'pointer',
                             opacity: survol == null || actif ? 1 : 0.55 }}
                    onMouseEnter={() => setSurvol(i)} onMouseLeave={() => setSurvol(null)} />
          );
          decalage += part;
          return el;
        })}
      </svg>
      <div style={{ position: 'absolute', inset: 0, display: 'grid',
                    placeItems: 'center', pointerEvents: 'none', textAlign: 'center' }}>
        <div>
          <div style={{ fontSize: taille * 0.19, fontWeight: 600,
                        letterSpacing: '-.03em', color: P.nuit, lineHeight: 1 }}>
            {courant ? courant.valeur : Math.round(anime)}
          </div>
          <div style={{ fontSize: 11.5, color: P.doux, marginTop: 4,
                        maxWidth: taille * 0.55, marginInline: 'auto', lineHeight: 1.25 }}>
            {courant ? courant.nom : legende}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Radar ────────────────────────────────────────────────────────────
   Le profil moyen, dimension par dimension. La ligne pointillée est le
   seuil : sans elle, une forme ne dit pas si elle est bonne. Un axe sous
   le seuil passe en point plein encre — la couleur seule ne suffit pas à
   le signaler à qui distingue mal le vert. */
export function Radar({ axes, seuil = 65, compact = false }) {
  const [ref, w] = useLargeur();
  const [survol, setSurvol] = useState(null);
  const n = axes.length;
  /* Le tracé cède de la place aux libellés plutôt que l'inverse : à
     R = 100 dans une carte de 316 px, il ne restait que 11 caractères
     par ligne, trop peu pour distinguer deux domaines dont les noms ne
     diffèrent qu'à la fin. Un radar un peu plus petit mais lisible vaut
     mieux qu'un grand radar dont on ne sait pas nommer les axes. */
  const R = Math.max(38, Math.min(w / 2 - (compact ? 48 : 92), compact ? 52 : 92));

  /* La longueur tolérée se déduit de la place RÉELLE, pas d'un nombre
     choisi au hasard : ce qui reste de part et d'autre du tracé, divisé
     par la largeur moyenne d'un caractère à 11,5 px. Dans une carte de
     316 px, vingt caractères sortaient de la carte. */
  const parLigne = Math.max(10, Math.min(20, Math.floor((w / 2 - R - 6) / 6.6)));
  const courts = useMemo(
    () => abregerListe(axes.map((a) => a.nom), parLigne * 2),
    [axes, parLigne],
  );

  const cx = w / 2;
  const cy = R + (compact ? 18 : 28);
  const H = 2 * R + (compact ? 36 : 86);   // de quoi loger trois lignes sous l'axe du bas
  const angle = (i) => -Math.PI / 2 + (i * 2 * Math.PI) / n;
  const pt = (i, v) => [cx + Math.cos(angle(i)) * R * v / 100,
                        cy + Math.sin(angle(i)) * R * v / 100];
  const poly = (vs) => vs.map((v, i) => pt(i, v).join(',')).join(' ');

  if (n < 3) {
    return (
      <p className="grid place-items-center py-10 text-[13px] text-[var(--text-muted)] text-center">
        Trois dimensions au moins sont nécessaires pour tracer un profil.
      </p>
    );
  }

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      {w > 0 && (
        <svg width={w} height={H} style={{ display: 'block', overflow: 'visible' }}
             role="img" aria-label="Profil moyen par dimension">
          {[100, 75, 50, 25].map((lv, j) => (
            <polygon key={lv} points={poly(Array(n).fill(lv))}
                     fill={j % 2 ? '#FFFFFF' : '#F6F9F7'} stroke={P.grille} />
          ))}
          {axes.map((_, i) => {
            const [ax, ay] = pt(i, 100);
            return <line key={i} x1={cx} y1={cy} x2={ax} y2={ay} stroke={P.grille} />;
          })}

          <polygon points={poly(Array(n).fill(seuil))} fill="none" stroke={P.nuit}
                   strokeDasharray="4 4" strokeWidth="1.3" opacity=".55" />

          <polygon points={poly(axes.map((a) => a.valeur))} fill={P.vert} fillOpacity=".2"
                   stroke={P.vert} strokeWidth="2.5" strokeLinejoin="round" />

          {axes.map((a, i) => {
            const [px, py] = pt(i, a.valeur);
            const sous = a.valeur < seuil;
            return (
              <circle key={a.nom} cx={px} cy={py} r={survol === i ? 6.5 : 4.5}
                      fill={sous ? P.nuit : '#FFFFFF'}
                      stroke={sous ? '#FFFFFF' : P.vert} strokeWidth="2.5"
                      style={{ transition: 'r .15s', cursor: 'pointer' }}
                      onMouseEnter={() => setSurvol(i)} onMouseLeave={() => setSurvol(null)} />
            );
          })}

          {!compact && axes.map((a, i) => {
            const [lx, ly] = pt(i, 118);
            const co = Math.cos(angle(i));
            const si = Math.sin(angle(i));
            const ancre = co > 0.3 ? 'start' : co < -0.3 ? 'end' : 'middle';
            const lignes = replier(courts[i], parLigne, 3);
            // Le bloc se centre verticalement sur le point de l'axe :
            // sinon une étiquette de deux lignes descend sous son axe.
            const y0 = ly + (si > 0.5 ? 10 : si < -0.5 ? -2 : 4)
                     - (lignes.length - 1) * 6;
            return (
              <text key={a.nom} x={lx} y={y0} textAnchor={ancre}
                    fontSize="11.5" fill={P.texte}>
                {lignes.map((l, j) => (
                  <tspan key={l} x={lx} dy={j ? 13 : 0}>
                    {l}
                    {j === lignes.length - 1 && (
                      <tspan fontWeight="600" dx="5"
                             fill={a.valeur < seuil ? P.alerte : P.nuit}>{a.valeur}</tspan>
                    )}
                  </tspan>
                ))}
              </text>
            );
          })}
        </svg>
      )}
      {survol != null && !compact && (
        <Infobulle x={pt(survol, axes[survol].valeur)[0]}
                   y={pt(survol, axes[survol].valeur)[1]}>
          <b>{axes[survol].nom} : {axes[survol].valeur}/100</b>
          <div style={{ color: 'rgba(255,255,255,.65)' }}>
            {axes[survol].valeur >= seuil
              ? 'Au-dessus du seuil'
              : `${seuil - axes[survol].valeur} points sous le seuil`}
          </div>
        </Infobulle>
      )}
    </div>
  );
}

/* ── Entonnoir ────────────────────────────────────────────────────────
   Des barres, pas un cône : un cône rétrécit en largeur ET en hauteur,
   si bien qu'une perte de 20 % y paraît une perte de moitié. Chaque
   étape porte son taux de passage depuis la précédente — c'est là que
   se lit la déperdition, pas dans la valeur absolue. */
export function Entonnoir({ etapes, compact = false }) {
  const max = Math.max(...etapes.map((e) => e.valeur), 1);
  const teintes = [P.nuit, P.nuit2, P.vertF, P.vert, P.vertC];
  return (
    <div className="flex flex-col w-full" style={{ gap: compact ? 7 : 16 }}>
      {etapes.map((e, i) => {
        const avant = i ? etapes[i - 1].valeur : null;
        const passage = avant ? Math.round((e.valeur / avant) * 100) : null;
        return (
          <div key={e.nom} className="flex flex-col gap-1.5">
            {!compact && (
              <div className="flex items-baseline gap-2">
                <span className="flex-1 text-[13.5px] font-medium text-[var(--text-bold)]">
                  {e.nom}
                </span>
                {passage != null && (
                  <span className="text-[11.5px] text-[var(--text-muted)]">
                    {passage}&nbsp;% de l’étape préc.
                  </span>
                )}
                <span className="text-[15px] font-semibold text-[var(--text-bold)]
                                 tabular-nums min-w-[30px] text-right">
                  {e.valeur}
                </span>
              </div>
            )}
            <div className="rounded-controle bg-[var(--bg-main)]"
                 style={{ height: compact ? 12 : 14 }}>
              <div className="h-full rounded-controle transition-[width] duration-700"
                   style={{ width: `${(e.valeur / max) * 100}%`,
                            background: teintes[i % teintes.length] }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Calendrier de fréquence ──────────────────────────────────────────
   Seize semaines en colonnes, sept jours en lignes. Les jours à venir
   sont hachurés plutôt que vides : une case blanche dans le futur
   voudrait dire « aucune séance », alors qu’elle veut dire « pas
   encore ». La nuance change la lecture de la moitié droite. */
const TEINTES_CAL = ['#EEF3EF', '#CDEAC5', '#9AD48C', '#5EBD4F', '#2F7A2A'];
const MOIS_COURT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin',
                    'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];

export function Calendrier({ semaines }) {
  const [ref, w] = useLargeur();
  const [survol, setSurvol] = useState(null);
  const id = useRef(nouvelId('hh')).current;

  const labW = 24;
  const gap = 4;
  const cell = Math.max(8, Math.min(26, Math.floor((w - labW - gap * 15) / 16)));
  const haut = 20;
  const H = haut + cell * 7 + gap * 6;
  const niveau = (v) => (v === 0 ? 0 : v === 1 ? 1 : v <= 3 ? 2 : v <= 5 ? 3 : 4);

  let moisPrecedent = -1;

  return (
    <div ref={ref} style={{ position: 'relative', width: '100%' }}>
      {w > 0 && (
        <svg width={labW + 16 * (cell + gap) - gap} height={H}
             style={{ display: 'block', overflow: 'visible' }}
             role="img" aria-label="Séances par jour sur seize semaines">
          <defs><Hachure id={id} /></defs>

          {['L', '', 'M', '', 'V', '', 'D'].map((l, d) => (l ? (
            <text key={d} x="0" y={haut + d * (cell + gap) + cell * 0.72}
                  fontSize="10.5" fill={P.doux}>{l}</text>
          ) : null))}

          {semaines.map((colonne, wk) => {
            const X = labW + wk * (cell + gap);
            const mois = new Date(colonne[0].date).getMonth();
            const etiquette = mois !== moisPrecedent;
            if (etiquette) moisPrecedent = mois;
            return (
              <g key={colonne[0].date}>
                {etiquette && (
                  <text x={X} y="11" fontSize="11" fill={P.doux}>{MOIS_COURT[mois]}</text>
                )}
                {colonne.map((c, d) => {
                  const actif = survol && survol[0] === wk && survol[1] === d;
                  return (
                    <rect key={c.date} x={X} y={haut + d * (cell + gap)}
                          width={cell} height={cell} rx={Math.min(6, cell / 3)}
                          fill={c.futur ? `url(#${id})` : TEINTES_CAL[niveau(c.valeur)]}
                          stroke={actif ? P.nuit : c.futur ? '#DCE4DF' : 'none'}
                          strokeWidth={actif ? 2 : 1}
                          onMouseEnter={() => !c.futur && setSurvol([wk, d])}
                          onMouseLeave={() => setSurvol(null)} />
                  );
                })}
              </g>
            );
          })}
        </svg>
      )}
      {survol && (
        <Infobulle x={labW + survol[0] * (cell + gap) + cell / 2}
                   y={haut + survol[1] * (cell + gap)}>
          <b>
            {semaines[survol[0]][survol[1]].valeur} séance
            {semaines[survol[0]][survol[1]].valeur > 1 ? 's' : ''}
          </b>
          <span style={{ color: 'rgba(255,255,255,.65)' }}>
            {' · '}
            {new Date(semaines[survol[0]][survol[1]].date)
              .toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'short' })}
          </span>
        </Infobulle>
      )}
    </div>
  );
}
